/**
 * Subcategory Slider — frontend behaviour.
 *
 * Desktop (≥1024px): the cards start stacked + fanned at the deck centre and
 * "deal out" into their natural flex-wrap grid positions, scrubbed to scroll
 * (GSAP ScrollTrigger). Only transforms animate, so it stays GPU-cheap.
 *
 * Mobile (<1024px): no animation — the deck becomes a Swiper slider.
 *
 * gsap.matchMedia() owns the desktop/mobile split and tears each context down
 * on resize across the 1024px boundary. The editor (window.wp.data) and
 * reduced-motion users get the static grid — the markup already renders it.
 */

import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Swiper from "swiper";
import { FreeMode, Navigation, Pagination } from "swiper/modules";

gsap.registerPlugin(ScrollTrigger);

const DESKTOP = "(min-width: 1024px)";
const MOBILE = "(max-width: 1023px)";
const MAX_ANGLE = 12; // outer cards fan out to ±12° in the compact stack.

// --- Desktop: deal cards out from a fanned stack into the grid --------------
function dealOut(root) {
  const deck = root.querySelector(".bskr-subcategory-slider__deck");
  const cards = Array.from(
    root.querySelectorAll(".bskr-subcategory-slider__card")
  );
  if (!deck || cards.length < 2) return;

  // Reduced motion: no pin, no deal-out — leave the static grid as-is.
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  // Measure each card's natural (grid) centre BEFORE any transform — the delta
  // to a single target point is how far to pull the card in for the stack.
  // Layout-relative (fixed card sizes), so it stays valid regardless of scroll.
  const deckRect = deck.getBoundingClientRect();
  const rects = cards.map((c) => c.getBoundingClientRect());
  const cx = deckRect.left + deckRect.width / 2;
  // Stack on the TOP row (not the deck centre) so it sits just under the
  // headline instead of leaving a tall gap above.
  const topY = Math.min(...rects.map((r) => r.top + r.height / 2));
  const n = cards.length;

  const offsets = rects.map((r, i) => ({
    x: cx - (r.left + r.width / 2),
    y: topY - (r.top + r.height / 2),
    rot: gsap.utils.mapRange(0, n - 1, MAX_ANGLE, -MAX_ANGLE, i),
  }));

  // Compact (stacked + fanned) start state.
  cards.forEach((card, i) => {
    gsap.set(card, {
      x: offsets[i].x,
      y: offsets[i].y,
      rotation: offsets[i].rot,
      transformOrigin: "center center",
    });
  });

  // Deal out to the grid (transform → 0), scrubbed to scroll — no pin.
  // Start late so the compact fanned deck stays in view for a good while
  // before it begins unfolding (the "wait" the design wants).
  gsap.to(cards, {
    x: 0,
    y: 0,
    rotation: 0,
    ease: "power2.out",
    // Reverse deal order: cards unfold from the edges inward to the centre.
    stagger: { each: 0.05, from: "edges" },
    scrollTrigger: {
      trigger: root,
      // Start even later — hold the stacked deck until the section reaches
      // the top of the viewport before it begins unfolding.
      start: "top top",
      end: "center 30%",
      scrub: 1,
      invalidateOnRefresh: true,
    },
  });

  // matchMedia reverts tweens/sets created here; clear inline transforms too
  // so the static grid stays pristine on teardown.
  return () => gsap.set(cards, { clearProps: "transform" });
}

// --- Mobile: Swiper slider --------------------------------------------------
function makeSwiper(root) {
  const deck = root.querySelector(".bskr-subcategory-slider__deck");
  const track = root.querySelector(".bskr-subcategory-slider__track");
  if (!deck || !track) return;

  const cards = track.querySelectorAll(
    ":scope > .bskr-subcategory-slider__card"
  );
  // A single card is not a slider — no Swiper, no nav/bullets.
  if (cards.length < 2) return;
  cards.forEach((c) => c.classList.add("swiper-slide"));

  const swiper = new Swiper(deck, {
    modules: [FreeMode, Navigation, Pagination],
    slidesPerView: "auto",
    spaceBetween: 16,
    // Mobile "center mode": the active slide (slide 1 at start) sits centred in
    // the viewport with the neighbours peeking. Disabled at ≥1024 so the
    // "Solo Slider" desktop variant stays left-aligned.
    centeredSlides: true,
    // Centre the slides when there aren't enough to fill the track, instead of
    // left-aligning them.
    centerInsufficientSlides: true,
    freeMode: { enabled: true, sticky: false, momentum: true },
    grabCursor: true,
    navigation: {
      prevEl: root.querySelector(".swiper-button-prev"),
      nextEl: root.querySelector(".swiper-button-next"),
    },
    pagination: {
      el: root.querySelector(".swiper-pagination"),
      clickable: true,
    },
    breakpoints: {
      1024: { spaceBetween: 30, centeredSlides: false },
    },
  });

  return () => {
    swiper.destroy(true, true);
    cards.forEach((c) => c.classList.remove("swiper-slide"));
  };
}

function initBlock(root) {
  if (root.dataset.subInited === "1") return;
  root.dataset.subInited = "1";

  // "Solo Slider": Swiper on every breakpoint, no scroll deal-out animation.
  if (root.classList.contains("is-only-slider")) {
    makeSwiper(root);
    return;
  }

  const mm = gsap.matchMedia();
  mm.add(DESKTOP, () => dealOut(root));
  mm.add(MOBILE, () => makeSwiper(root));
}

function init() {
  // Skip inside the Gutenberg editor — the static grid is the editor preview.
  if (typeof window !== "undefined" && window.wp && window.wp.data) return;
  document
    .querySelectorAll(".wp-block-bskr-subcategory-slider")
    .forEach(initBlock);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
